import React, { useEffect, useState, useRef } from 'react';
import DataTable from 'react-data-table-component';
import { GET_AEPS_WITHDRAWAL_REPORT, GET_AEPS_WITHDRAWAL_INQUIRY, FING_PAY_CASH_WITHDRAWAL_CHECK_STATUS_API } from './../../../Utils/appConstants';
import { getRequest, postRequest } from './../../../Utils/AppApi';
import { useHistory } from "react-router-dom";
import { DatePicker, Tooltip, Select, Space, Tag } from 'antd';
import { getUTCToLocalDateTimeFormat, toDayDate, queryDate } from '../../../Utils/function';
import { CSVLink } from 'react-csv';
import { aepsServiceInArray, aepsStatus, aepsStatusInArray, aepsApiServiceInArray } from '../../../Utils/Common';
import { toast } from 'react-toastify';
import PropagateLoader from "react-spinners/PropagateLoader";
import {
    PrinterOutlined,
    SearchOutlined,
    ClearOutlined,
    DownloadOutlined,
    FilterOutlined,
} from "@ant-design/icons";
import moment from 'moment';

/* ============================================================
   PERSISTENCE HELPERS
============================================================ */
const FILTERS_STORAGE_KEY = 'aepsHistoryReportFilters';
const RESTORE_FLAG_KEY = 'aepsHistoryReportRestore';

const consumeRestoreFlag = () => {
    try {
        const flag = sessionStorage.getItem(RESTORE_FLAG_KEY);
        if (flag === '1') {
            sessionStorage.removeItem(RESTORE_FLAG_KEY);
            return true;
        }
    } catch { /* ignore */ }
    return false;
};

const loadSavedFilters = () => {
    if (!consumeRestoreFlag()) {
        try { sessionStorage.removeItem(FILTERS_STORAGE_KEY); } catch { /* ignore */ }
        return {};
    }
    try {
        const raw = sessionStorage.getItem(FILTERS_STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

const restoreDate = (str) => {
    if (!str) return toDayDate();
    const m = moment(str, 'DD-MM-YYYY');
    return m.isValid() ? m : toDayDate();
};

/* ============================================================
   PROVIDER HELPERS
============================================================ */
const isFingPay = (record) => record?.apiServiceName === 'FINGPAY';
const isInstantPay = (record) =>
    !record?.apiServiceName || record.apiServiceName === 'INSTANTPAY';

const getInquiryUrl = (record) =>
    isFingPay(record) ? FING_PAY_CASH_WITHDRAWAL_CHECK_STATUS_API : GET_AEPS_WITHDRAWAL_INQUIRY;

/* ⭐ Short name — used in table and CSV */
const getApiProviderShort = (record) => (isFingPay(record) ? 'FP' : 'IP');
const getApiProviderFull  = (record) => (isFingPay(record) ? 'FINGPAY' : 'INSTANTPAY');

/* ⭐ Short-label options for the API Service filter dropdown.
   - value stays FINGPAY / INSTANTPAY (backend unchanged)
   - label renders as FP / IP with a hover tooltip showing the full name */
const aepsApiServiceShortOptions = (aepsApiServiceInArray || []).map((opt) => {
    const rawValue  = String(opt.value ?? '');
    const fullLabel = opt.label ?? rawValue;
    const short =
        /FING/i.test(rawValue)    ? 'FP'
        : /INSTANT/i.test(rawValue) ? 'IP'
        : fullLabel;

    return {
        value: opt.value,
        label: (
            <Tooltip title={fullLabel} placement="right">
                <span style={{ fontWeight: 600 }}>{short}</span>
            </Tooltip>
        ),
        short,
        full: fullLabel,
    };
});

/* Plain-text version of aepsStatus() for CSV export. */
const getStatusText = (val) => {
    if (val === 0) return 'Status Unknown';
    if (val === 1) return 'Success';
    if (val === 2) return 'Failed';
    return '-';
};

/* ============================================================
   MODERN STYLES (scoped)
============================================================ */
const reportStyles = `
.aeps-page {
    padding: 16px 24px 40px;
    background: #f5f7fb;
    min-height: calc(100vh - 60px);
}
.aeps-page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;
    flex-wrap: wrap;
    gap: 12px;
}
.aeps-page-title {
    font-size: 22px;
    font-weight: 700;
    color: #1f2a44;
    margin: 0;
    letter-spacing: 0.2px;
}
.aeps-page-title span { color: #e5484d; }
.aeps-page-subtitle {
    font-size: 13px;
    color: #6b7280;
    margin: 2px 0 0;
}

.aeps-card {
    background: #ffffff;
    border-radius: 14px;
    box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 4px 16px rgba(16,24,40,.06);
    border: 1px solid #eef0f4;
    overflow: hidden;
}
.aeps-card + .aeps-card { margin-top: 18px; }

.aeps-card-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 20px;
    border-bottom: 1px solid #f0f2f6;
    background: linear-gradient(180deg, #fbfcfe 0%, #ffffff 100%);
}
.aeps-card-head-title {
    font-size: 13px;
    font-weight: 600;
    color: #344054;
    text-transform: uppercase;
    letter-spacing: .6px;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
}
.aeps-card-head-title .anticon {
    color: #5851D2;
    font-size: 14px;
}

.aeps-filter-body { padding: 18px 20px 20px; }

.aeps-filter-grid {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 14px 16px;
    align-items: end;
}

.aeps-field { display: flex; flex-direction: column; min-width: 0; }
.aeps-field label {
    font-size: 11.5px;
    font-weight: 600;
    color: #667085;
    text-transform: uppercase;
    letter-spacing: .4px;
    margin-bottom: 6px;
}

.aeps-field .ant-picker,
.aeps-field .ant-select,
.aeps-field .aeps-input {
    width: 100%;
    height: 40px;
    border-radius: 10px;
    border: 1px solid #e4e7ec;
    background: #fff;
    transition: border-color .15s, box-shadow .15s;
    font-size: 13.5px;
}
.aeps-field .ant-picker { padding: 0 11px; }
.aeps-field .ant-picker input { font-size: 13.5px; }
.aeps-field .ant-select .ant-select-selector {
    height: 40px !important;
    border-radius: 10px !important;
    border: 1px solid #e4e7ec !important;
    padding: 0 11px !important;
}
.aeps-field .ant-select-single .ant-select-selector .ant-select-selection-item,
.aeps-field .ant-select-single .ant-select-selector .ant-select-selection-placeholder {
    line-height: 38px !important;
    font-size: 13.5px;
}
.aeps-field .ant-picker:hover,
.aeps-field .ant-select:hover .ant-select-selector,
.aeps-field .aeps-input:hover { border-color: #c7cbd3 !important; }
.aeps-field .ant-picker-focused,
.aeps-field .ant-select-focused .ant-select-selector,
.aeps-field .aeps-input:focus {
    border-color: #5851D2 !important;
    box-shadow: 0 0 0 3px rgba(88,81,210,.12) !important;
    outline: none;
}

.aeps-input {
    padding: 0 12px;
    color: #101828;
    outline: none;
}
.aeps-input::placeholder { color: #98a2b3; }

.aeps-filter-grid .aeps-actions {
    grid-column: span 3;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: flex-end;
    gap: 10px;
    min-height: 40px;
}

.aeps-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 20px;
    border-radius: 10px;
    border: 1px solid transparent;
    font-size: 13.5px;
    font-weight: 600;
    cursor: pointer;
    transition: transform .08s, box-shadow .15s, background .15s, border-color .15s;
    white-space: nowrap;
}
.aeps-btn:active { transform: translateY(1px); }

.aeps-btn-primary {
    background: linear-gradient(180deg, #6b64e0 0%, #5851D2 100%);
    color: #fff;
    box-shadow: 0 2px 6px rgba(88,81,210,.35);
}
.aeps-btn-primary:hover { box-shadow: 0 4px 12px rgba(88,81,210,.45); }

.aeps-btn-ghost {
    background: #ffffff;
    color: #475467;
    border-color: #e4e7ec;
}
.aeps-btn-ghost:hover { background: #f9fafb; border-color: #cfd4dc; }

.aeps-btn-success {
    background: #ffffff;
    color: #0e9384;
    border-color: #b7e4dd;
}
.aeps-btn-success:hover { background: #f0fdfa; border-color: #0e9384; }

.aeps-table-wrap { padding: 4px 6px 10px; }
.aeps-table-wrap .rdt_TableHeadRow {
    background: #f9fafb !important;
    border-bottom: 1px solid #eef0f4 !important;
    min-height: 46px !important;
}
.aeps-table-wrap .rdt_TableCol {
    font-size: 11.5px !important;
    font-weight: 700 !important;
    color: #475467 !important;
    text-transform: uppercase !important;
    letter-spacing: .4px !important;
}
.aeps-table-wrap .rdt_TableRow {
    border-bottom: 1px solid #f2f4f7 !important;
    min-height: 52px !important;
    transition: background .15s;
}
.aeps-table-wrap .rdt_TableRow:hover { background: #fafbff !important; }
.aeps-table-wrap .rdt_TableCell { font-size: 13px !important; color: #344054 !important; }
.aeps-table-wrap .rdt_Pagination {
    border-top: 1px solid #eef0f4 !important;
    background: #fff !important;
    min-height: 52px !important;
}

@media (max-width: 1023px) {
    .aeps-filter-grid {
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    }
    .aeps-filter-grid .aeps-actions {
        grid-column: 1 / -1;
        margin-top: 6px;
        padding-top: 16px;
        border-top: 1px dashed #eef0f4;
    }
}

@media (max-width: 640px) {
    .aeps-page { padding: 12px; }
    .aeps-filter-grid .aeps-actions { justify-content: center; }
    .aeps-filter-grid .aeps-actions .aeps-btn {
        flex: 1 1 auto;
        justify-content: center;
    }
}
`;

/* ============================================================ */

function AepsHistoryReport(props) {

    const history = useHistory();
    const csvLink = useRef();
    const limit = 10;
    const dateFormat = 'DD-MM-YYYY';

    const [saved] = useState(() => loadSavedFilters());

    const [searchUserId, setSearchUserId] = useState(saved.searchUserId ?? null);
    const [searchTransId, setSearchTransId] = useState(saved.searchTransId ?? null);
    const [searchMobile, setSearchMobile] = useState(saved.searchMobile ?? null);
    const [searchServiceName, setSearchServiceName] = useState(saved.searchServiceName ?? null);
    const [searchStatus, setSearchStatus] = useState(saved.searchStatus ?? null);
    const [searchAadhar, setSearchAadhar] = useState(saved.searchAadhar ?? null);
    const [searchApiService, setSearchApiService] = useState(saved.searchApiService ?? null);
    const [endDate, setEndDate] = useState(restoreDate(saved.endDate));
    const [startDate, setStartDate] = useState(restoreDate(saved.startDate));

    const [getData, setData] = useState([]);
    const [getCsvData, setCsvData] = useState([]);
    const [getTotalRecord, setTotalRecord] = useState(0);
    const [page, setPage] = useState(saved.page || 1);
    const [isLimit, setLimit] = useState(saved.isLimit || limit);
    const [isLoading, setLoading] = useState(false);

    const handlePrintInvoice = async (record) => {
        try { sessionStorage.setItem(RESTORE_FLAG_KEY, '1'); } catch { /* ignore */ }

        if (record.status === 0 && record.apiServiceName !== 'FINGPAY') {
            setLoading(true);
            try {
                const payload = { transactionId: record.transactionId };
                const URL = getInquiryUrl(record);
                const response = await postRequest(URL, payload, history);
                if (response.success === true) {
                    toast.success(response.message);
                    history.push(`/aepsinvoice/${record.transactionId}`);
                } else {
                    toast.error(response.message);
                }
            } catch (error) {
                console.log(error);
                toast.error("Failed to process inquiry");
            } finally {
                setLoading(false);
            }
        } else {
            history.push(`/aepsinvoice/${record.transactionId}`);
        }
    };

    const columns = [{
        name: 'Date Time',
        cell: row => getUTCToLocalDateTimeFormat(row.createdAt),
        width: "180px",
    }, {
        name: 'Trans ID',
        selector: row => <Tooltip placement="bottom" title={row.transactionId}>{row.transactionId}</Tooltip>,
        width: "180px",
    }, {
        name: 'Reference ID',
        selector: row => row.userData && row.userData.username,
        width: "120px",
    }, {
        name: 'Service Name',
        selector: row => <Tooltip placement="bottom" title={row.mode}>{row.mode}</Tooltip>,
        width: "180px",
    },
    // ⭐ API Provider column — short FP / IP with hover tooltip
    {
        name: 'API Provider',
        cell: row => {
            const short = getApiProviderShort(row);
            const full  = getApiProviderFull(row);
            return (
                <Tooltip placement="bottom" title={full}>
                    <Tag
                        color={short === 'FP' ? 'blue' : 'green'}
                        style={{ fontSize: '11px', fontWeight: 600, margin: 0 }}
                    >
                        {short}
                    </Tag>
                </Tooltip>
            );
        },
        width: "120px",
        center: true,
    }, {
        name: 'Name',
        selector: row => <Tooltip placement="bottom" title={row.userData && row.userData.name}>{row.userData && row.userData.name}</Tooltip>,
        width: "180px",
    }, {
        name: 'Bank',
        selector: row => (!row.bankName) ? "-" : row.bankName,
        width: "130px",
    }, {
        name: 'Aadhaar',
        selector: row => (!row.aadhaar) ? "-" : "XXXXXXXX" + row.aadhaar,
        width: "130px",
    }, {
        name: 'Mobile',
        selector: row => row.mobile,
        width: "130px",
    }, {
        name: 'Amount',
        selector: row => row.amount ? row.amount : 0,
        width: "100px",
    }, {
        name: 'Bank RRN',
        selector: row => row.operatorId ? row.operatorId : row.bankRRN,
        width: "130px",
    }, {
        name: 'Status',
        selector: row => aepsStatus(row.status),
        width: "140px",
    }, {
        name: 'Action',
        cell: (record, key) => (
            <Space key={key} size="middle">
                {(record.status === 0 && record.apiServiceName !== 'FINGPAY') ? (
                    <Tag
                        color="gold"
                        onClick={() => transactionInquiry(record)}
                        className="pointer"
                        style={{ borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
                    >
                        INQUIRY
                    </Tag>
                ) : "-"}

                {(record.apiServiceName !== 'FINGPAY') ? (
                    <Tooltip title="Print Invoice">
                        <PrinterOutlined
                            className="pointer"
                            style={{ fontSize: "16px", color: "#5851D2" }}
                            onClick={() => handlePrintInvoice(record)}
                        />
                    </Tooltip>) : '-'}
            </Space>
        )
    }, {
        name: 'Message',
        selector: row => <Tooltip placement="bottom" title={row.message}>{row.message}</Tooltip>,
        width: "200px",
    }];

    const transactionInquiry = (data) => {
        setLoading(true);
        const payload = { transactionId: data.transactionId };
        const URL = getInquiryUrl(data);

        postRequest(`${URL}`, payload, history).then((response) => {
            if (response.success === true) {
                toast.success(response.message);
            } else {
                toast.error(response.message);
            }
            getDataReports();
            setLoading(false);
        }).catch(function (error) {
            console.log(error);
            setLoading(false);
        });
    };

    const objectToQueryString = (obj) => {
        var str = [];
        for (var p in obj)
            if (obj.hasOwnProperty(p)) {
                str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
            }
        return str.join("&");
    };

    const params = { page: page, limit: isLimit };

    const getDataReports = (data) => {
        if (data) {
            setLimit(data);
            params.limit = data;
        }
        if (startDate && endDate) {
            params.startDate = startDate ? queryDate(startDate) : "";
            params.endDate = endDate ? queryDate(endDate) : "";
        }
        params.searchUserId = searchUserId ? searchUserId : "";
        params.searchTransId = searchTransId ? searchTransId : "";
        params.searchMobile = searchMobile ? searchMobile : "";
        params.searchServiceName = searchServiceName ? searchServiceName : "";
        params.searchStatus = searchStatus ? searchStatus : "";
        params.searchAadhar = searchAadhar ? searchAadhar : "";
        // ⭐ API filter still passes the FULL value (FINGPAY / INSTANTPAY)
        params.searchApiService = searchApiService ? searchApiService : "";

        const queryString = objectToQueryString(params);
        getRequest(`${GET_AEPS_WITHDRAWAL_REPORT}?${queryString}`, history).then((response) => {
            if (response.success === true) {
                setData(response.data);
                setTotalRecord(response.data.totalDocs);
                setPage(response.data.page);
            } else {
                toast.error(response.message);
                setData([]);
                setTotalRecord(0);
                setPage(1);
            }
        }).catch(function (error) {
            console.log(error);
        });
    } // eslint-disable-line react-hooks/exhaustive-deps

    const getCSVDataList = () => {
        let params = {};
        params.exportsCsv = true;
        if (startDate && endDate) {
            params.startDate = startDate ? queryDate(startDate) : "";
            params.endDate = endDate ? queryDate(endDate) : "";
        }
        params.searchUserId = searchUserId ? searchUserId : "";
        params.searchTransId = searchTransId ? searchTransId : "";
        params.searchMobile = searchMobile ? searchMobile : "";
        params.searchServiceName = searchServiceName ? searchServiceName : "";
        params.searchStatus = searchStatus ? searchStatus : "";
        params.searchAadhar = searchAadhar ? searchAadhar : "";
        // ⭐ API filter still passes the FULL value (FINGPAY / INSTANTPAY)
        params.searchApiService = searchApiService ? searchApiService : "";

        const queryString = objectToQueryString(params);
        getRequest(`${GET_AEPS_WITHDRAWAL_REPORT}?${queryString}`, history).then((response) => {
            if (response.success === true) {
                let csvdataRecord = [];
                let csvdata = response.data;
                csvdata.map((e) => {
                    return csvdataRecord.push({
                        "Date Time": getUTCToLocalDateTimeFormat(e.createdAt),
                        "Trans ID": e.transactionId,
                        "Reference ID": e.userData && e.userData.username,
                        "Service Name": e.mode,
                        "API Provider": getApiProviderShort(e),
                        "Name": e.userData && e.userData.name,
                        "Bank": e.bankName,
                        "Aadhaar": (!e.aadhaar) ? "-" : "XXXXXXXX" + e.aadhaar,
                        "Mobile": e.mobile,
                        "Amount": e.amount,
                        "Bank RRN": e.operatorId,
                        "Status": getStatusText(e.status),
                        "Message": e.message,
                        "Enq1 Message": e.enq1Message,
                        "Enq2 Message": e.enq2Message
                    });
                });
                setCsvData(csvdataRecord);
                setTimeout(() => {
                    if (csvLink.current && csvLink.current.link) {
                        csvLink.current.link.click();
                    }
                }, 1000);
            } else {
                setCsvData([]);
            }
        }).catch(function (error) {
            console.log(error);
        });
    };

    const handlePageChange = (page, size) => {
        setPage(page);
    };

    const onChangeStartDate = (date) => { setStartDate(date); };
    const onChangeEndDate = (date) => { setEndDate(date); };

    const handleSearch = () => { getDataReports(); };

    const handleClear = () => {
        try {
            sessionStorage.removeItem(FILTERS_STORAGE_KEY);
            sessionStorage.removeItem(RESTORE_FLAG_KEY);
        } catch { /* ignore */ }

        setStartDate(null);
        setEndDate(null);
        setSearchAadhar(null);
        setSearchMobile(null);
        setSearchServiceName(null);
        setSearchStatus(null);
        setSearchTransId(null);
        setSearchUserId(null);
        setSearchApiService(null);
        setPage(1);
    };

    /* ============================================================
       PERSIST FILTERS
    ============================================================ */
    useEffect(() => {
        const toSave = {
            startDate: startDate ? moment(startDate).format(dateFormat) : null,
            endDate: endDate ? moment(endDate).format(dateFormat) : null,
            searchUserId,
            searchTransId,
            searchMobile,
            searchServiceName,
            searchStatus,
            searchAadhar,
            searchApiService,          // ⭐ stored as FINGPAY / INSTANTPAY
            page,
            isLimit,
        };
        try {
            sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(toSave));
        } catch { /* ignore */ }
    }, [
        startDate, endDate,
        searchUserId, searchTransId, searchMobile, searchServiceName,
        searchStatus, searchAadhar, searchApiService,
        page, isLimit,
    ]);
    /* ============================================================ */

    useEffect(() => { handleSearch(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { getDataReports(); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

    if (isLoading) {
        return (
            <div className="content text-center" style={{ margin: "250px 0" }}>
                <PropagateLoader color="#5851D2" loading={isLoading} size={25} />
            </div>
        );
    }

    return (
        <div className="aeps-page">
            <style>{reportStyles}</style>

            <div className="aeps-card">
                <div className="aeps-filter-body">
                    <div className="aeps-filter-grid">
                        <div className="aeps-field">
                            <label>From Date</label>
                            <DatePicker
                                value={startDate}
                                format={dateFormat}
                                onChange={onChangeStartDate}
                                placeholder="Select from date"
                            />
                        </div>

                        <div className="aeps-field">
                            <label>To Date</label>
                            <DatePicker
                                value={endDate}
                                format={dateFormat}
                                onChange={onChangeEndDate}
                                placeholder="Select to date"
                            />
                        </div>

                        <div className="aeps-field">
                            <label>User ID</label>
                            <input
                                type="text"
                                className="aeps-input"
                                placeholder="Enter User ID"
                                onChange={(e) => setSearchUserId(e.target.value)}
                                value={searchUserId || ''}
                            />
                        </div>

                        <div className="aeps-field">
                            <label>Transaction ID</label>
                            <input
                                type="text"
                                className="aeps-input"
                                placeholder="Enter Transaction ID"
                                onChange={(e) => setSearchTransId(e.target.value)}
                                value={searchTransId || ''}
                            />
                        </div>

                        <div className="aeps-field">
                            <label>Aadhaar Number</label>
                            <input
                                type="text"
                                className="aeps-input"
                                placeholder="Enter Aadhaar number"
                                onChange={(e) => setSearchAadhar(e.target.value)}
                                value={searchAadhar || ''}
                            />
                        </div>

                        <div className="aeps-field">
                            <label>Mobile Number</label>
                            <input
                                type="text"
                                className="aeps-input"
                                placeholder="Enter Mobile number"
                                onChange={(e) => setSearchMobile(e.target.value)}
                                value={searchMobile || ''}
                            />
                        </div>

                        <div className="aeps-field">
                            <label>Service Name</label>
                            <Select
                                options={aepsServiceInArray}
                                value={searchServiceName || undefined}
                                className="normal-light"
                                classNamePrefix="select"
                                placeholder="Select service name"
                                name="mode"
                                onChange={(e) => setSearchServiceName(e)}
                                allowClear
                            />
                        </div>

                        <div className="aeps-field">
                            <label>Status</label>
                            <Select
                                options={aepsStatusInArray}
                                value={searchStatus || undefined}
                                className="normal-light"
                                classNamePrefix="select"
                                placeholder="Select status"
                                name="status"
                                onChange={(e) => setSearchStatus(e)}
                                allowClear
                            />
                        </div>

                        {/* ⭐ API Service filter — shows FP / IP, passes full value */}
                        <div className="aeps-field">
                            <label>API Service</label>
                            <Select
                                options={aepsApiServiceShortOptions}
                                value={searchApiService || undefined}
                                className="normal-light"
                                classNamePrefix="select"
                                placeholder="Select API (FP / IP)"
                                name="apiService"
                                onChange={(e) => setSearchApiService(e)}
                                allowClear
                            />
                        </div>

                        <div className="aeps-actions">
                            <button type="button" className="aeps-btn aeps-btn-ghost" onClick={handleClear}>
                                <ClearOutlined /> Clear
                            </button>

                            <button type="button" className="aeps-btn aeps-btn-success" onClick={getCSVDataList}>
                                <DownloadOutlined /> Export CSV
                            </button>

                            <button type="button" className="aeps-btn aeps-btn-primary" onClick={handleSearch}>
                                <SearchOutlined /> Search
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="aeps-card">
                <div className="aeps-table-wrap">
                    <DataTable
                        title={""}
                        columns={columns}
                        data={getData.docs}
                        highlightOnHover
                        pagination
                        paginationServer
                        paginationTotalRows={getTotalRecord}
                        paginationDefaultPage={page}
                        paginationPerPage={isLimit}
                        onChangeRowsPerPage={getDataReports}
                        onChangePage={handlePageChange}
                        noDataComponent={
                            <div style={{ padding: '40px 20px', color: '#98a2b3', fontSize: 13 }}>
                                No records found for the selected filters.
                            </div>
                        }
                    />
                </div>
            </div>

            <CSVLink
                data={getCsvData}
                filename='aeps-withdrawal-report'
                className='hidden'
                ref={csvLink}
                target='_blank'
                style={{ display: 'none' }}
            />
        </div>
    );
}

export default AepsHistoryReport;